import { parse } from "yaml";
import * as z from "zod";
import { contentHasAudioDirective, contentHasVideoDirective } from "./blog-markdown-directives";
import { isPublicPost, isScheduledPost } from "./blog-visibility";
import { announcementSchema, announcementWindowError } from "./announcement";
import { SITE_DEFAULT_LICENSE, licenseIds } from "./license";
import type { Locale, SiteContext } from "./site";
import { locales, markets } from "./site";

// Re-exported so existing call sites (blog pages, tests) keep working unchanged;
// see ./blog-visibility for why these two live in their own module.
export { isPublicPost, isScheduledPost };

const postSchema = z
  .object({
    storyId: z.string().min(1),
    locale: z.enum(locales),
    slug: z.string().min(1),
    title: z.string().min(1),
    excerpt: z.string().min(1),
    publishedAt: z.string().date(),
    markets: z.array(z.enum(markets)).min(1),
    heroImage: z.union([z.string().url(), z.string().regex(/^\/[^\s]+$/)]).optional(),
    // Optional "listen to this brief" audio, rendered in the post header. The path
    // points at a self-hosted file in /public; the title is the player's own label.
    audioBrief: z.union([z.string().url(), z.string().regex(/^\/[^\s]+$/)]).optional(),
    audioBriefTitle: z.string().optional(),
    seoTitle: z.string().min(1),
    seoDescription: z.string().min(1),
    tags: z.array(z.string()).default([]),
    announcement: announcementSchema.optional(),
    // Republishing terms. Defaults to the site-wide license (CC BY 4.0); third-party
    // material we host with permission overrides this to "all-rights-reserved".
    license: z.enum(licenseIds).default(SITE_DEFAULT_LICENSE),
    published: z.boolean().default(true),
  })
  .superRefine((post, ctx) => {
    if (!post.announcement) return;
    const message = announcementWindowError(post.announcement, post.publishedAt);
    if (message) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["announcement"], message });
    }
  });

// Drafts (published: false) may carry rough/incomplete frontmatter so they can be
// committed mid-write without breaking the build. Only slug, title and locale are
// required; everything else falls back to a sane default so the post still renders
// in preview. Drafts default to every market so they show in any locale's preview
// regardless of audience targeting.
const draftPostSchema = z
  .object({
    storyId: z.string().min(1).optional(),
    locale: z.enum(locales),
    slug: z.string().min(1),
    title: z.string().min(1),
    excerpt: z.string().optional(),
    publishedAt: z.string().optional(),
    markets: z.array(z.enum(markets)).optional(),
    heroImage: z.union([z.string().url(), z.string().regex(/^\/[^\s]+$/)]).optional(),
    audioBrief: z.union([z.string().url(), z.string().regex(/^\/[^\s]+$/)]).optional(),
    audioBriefTitle: z.string().optional(),
    seoTitle: z.string().optional(),
    seoDescription: z.string().optional(),
    tags: z.array(z.string()).default([]),
    announcement: z.unknown().optional(),
    license: z.enum(licenseIds).optional(),
    published: z.literal(false),
  })
  .transform((d) => {
    const parsedAnnouncement = announcementSchema.safeParse(d.announcement);
    return {
      storyId: d.storyId ?? d.slug,
      locale: d.locale,
      slug: d.slug,
      title: d.title,
      excerpt: d.excerpt ?? "",
      publishedAt: d.publishedAt ?? "2099-12-31",
      markets: d.markets ?? [...markets],
      heroImage: d.heroImage,
      audioBrief: d.audioBrief,
      audioBriefTitle: d.audioBriefTitle,
      seoTitle: d.seoTitle ?? d.title,
      seoDescription: d.seoDescription ?? d.excerpt ?? d.title,
      tags: d.tags,
      announcement: parsedAnnouncement.success ? parsedAnnouncement.data : undefined,
      license: d.license ?? SITE_DEFAULT_LICENSE,
      published: false as const,
    };
  });

// hasAudio/hasVideo are derived at parse time (not frontmatter): they let post
// summaries surface a media indicator without shipping the full content.
export type BlogPost = z.infer<typeof postSchema> & {
  content: string;
  hasAudio: boolean;
  hasVideo: boolean;
};
export type BlogPostSummary = Omit<BlogPost, "content">;
export type LocalizedPostLink = Pick<BlogPost, "locale" | "slug" | "title">;

export interface BlogRepository {
  listPosts(context: SiteContext): Promise<BlogPostSummary[]>;
  getPost(context: SiteContext, slug: string): Promise<BlogPost | null>;
  getTranslations(storyId: string): Promise<LocalizedPostLink[]>;
}

const markdownFiles = import.meta.glob("../../content/blog/**/*.md", {
  eager: true,
  query: "?raw",
  import: "default",
}) as Record<string, string>;

// Drafts and scheduled posts are visible only under `vite dev` (local preview)
// and on builds that opt in with VITE_INCLUDE_DRAFTS=true (the staging image —
// see Makefile/Dockerfile). Production builds leave it unset, so drafts are
// skipped before they are even parsed, while future-dated published posts stay
// bundled but remain hidden until their UTC publish date arrives.
export const SHOW_DRAFTS =
  import.meta.env.DEV ||
  (import.meta.env as unknown as Record<string, string | undefined>).VITE_INCLUDE_DRAFTS === "true";
export const SHOW_PREVIEW_POSTS = SHOW_DRAFTS;

const FRONTMATTER = /^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/;

type BlogVisibilityOptions = {
  preview?: boolean;
  now?: Date;
};

function canShowPost(post: BlogPost, options?: BlogVisibilityOptions): boolean {
  if (options?.preview ?? SHOW_PREVIEW_POSTS) return true;
  return isPublicPost(post, options?.now);
}

function isDraftSource(source: string): boolean {
  const match = /^---\r?\n([\s\S]*?)\r?\n---/.exec(source);
  if (!match) return false;
  try {
    const data = parse(match[1]) as Record<string, unknown> | null;
    return data?.published === false;
  } catch {
    return false;
  }
}

export function parseBlogPost(source: string, options?: { draft?: boolean }): BlogPost {
  const match = FRONTMATTER.exec(source);
  if (!match) throw new Error("Blog post is missing YAML frontmatter");
  const data = parse(match[1]);
  const parsed = options?.draft ? draftPostSchema.parse(data) : postSchema.parse(data);
  const content = match[2].trim();
  return {
    ...parsed,
    content,
    hasAudio: Boolean(parsed.audioBrief) || contentHasAudioDirective(content),
    hasVideo: contentHasVideoDirective(content),
  };
}

// Pure, env-independent loader so the draft visibility rule is unit-testable.
export function loadPosts(sources: string[], showDrafts: boolean): BlogPost[] {
  return sources.flatMap((source) => {
    const draft = isDraftSource(source);
    if (draft && !showDrafts) return [];
    try {
      return [parseBlogPost(source, { draft })];
    } catch (error) {
      if (draft) {
        console.warn("[blog] skipping draft with invalid frontmatter:", error);
        return [];
      }
      throw error;
    }
  });
}

export const posts = loadPosts(Object.values(markdownFiles), SHOW_DRAFTS);

function isVisible(post: BlogPost, context: SiteContext, options?: BlogVisibilityOptions): boolean {
  return (
    post.locale === context.locale &&
    post.markets.includes(context.market) &&
    canShowPost(post, options)
  );
}

export const markdownBlogRepository: BlogRepository = {
  async listPosts(context) {
    return posts
      .filter((post) => isVisible(post, context))
      .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt))
      .map(({ content: _content, ...post }) => post);
  },
  async getPost(context, slug) {
    return posts.find((post) => post.slug === slug && isVisible(post, context)) ?? null;
  },
  async getTranslations(storyId) {
    return posts
      .filter((post) => post.storyId === storyId && canShowPost(post))
      .map(({ locale, slug, title }) => ({ locale, slug, title }));
  },
};

export function listPosts(context: SiteContext): BlogPostSummary[] {
  return posts
    .filter((post) => isVisible(post, context))
    .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt))
    .map(({ content: _content, ...post }) => post);
}

export function getPost(context: SiteContext, slug: string): BlogPost | null {
  return posts.find((post) => post.slug === slug && isVisible(post, context)) ?? null;
}

export function getPostNeighbors(
  context: SiteContext,
  slug: string,
): { newer: BlogPostSummary | null; older: BlogPostSummary | null } {
  const visiblePosts = listPosts(context);
  const currentIndex = visiblePosts.findIndex((post) => post.slug === slug);

  if (currentIndex === -1) return { newer: null, older: null };

  return {
    newer: currentIndex > 0 ? visiblePosts[currentIndex - 1] : null,
    older: currentIndex < visiblePosts.length - 1 ? visiblePosts[currentIndex + 1] : null,
  };
}

export function getTranslations(storyId: string): LocalizedPostLink[] {
  return posts
    .filter((post) => post.storyId === storyId && canShowPost(post))
    .map(({ locale, slug, title }) => ({ locale, slug, title }));
}

export function getTranslationPath(storyId: string, locale: Locale): string {
  const translation = getTranslations(storyId).find((post) => post.locale === locale);
  return translation ? `/blog/${translation.slug}` : "/blog";
}
